import { supabase } from './supabaseClient';

// 카테고리별 MDI 아이콘 매핑
export const getCategoryEmoji = (category) => {
  const categoryIcons = {
    '기술': 'mdi:robot-outline',
    '비즈니스': 'mdi:briefcase-outline',
    '정책': 'mdi:bank-outline',
    '회계': 'mdi:calculator-variant-outline',
    '분석': 'mdi:trending-up',
    '마케팅': 'mdi:bullhorn-outline',
    '체험': 'mdi:target',
    '상담': 'mdi:account-tie-outline',
    '성공사례': 'mdi:trophy-outline',
    '기타': 'mdi:newspaper-variant-outline'
  };

  return categoryIcons[category] || 'mdi:newspaper-variant-outline';
};

// URL에서 도메인 추출 (source 필드용)
const extractDomainFromUrl = (url) => {
  if (!url || url === '#') return '출처';
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch (error) {
    return '출처';
  }
};

// 웹훅에서 뉴스 데이터 가져오기
export const fetchNewsFromWebhook = async () => {
  try {
    const webhookUrl = 'https://bshong-n8n.ngrok.io/webhook/054af103-1da4-45d6-acd1-97adb4fd26d7';
    
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // 웹훅 응답이 배열인지 객체인지 확인
    let newsArray = [];
    if (Array.isArray(data)) {
      newsArray = data;
    } else if (data.news && Array.isArray(data.news)) {
      newsArray = data.news;
    } else if (data.data && Array.isArray(data.data)) {
      newsArray = data.data;
    } else if (data.records && Array.isArray(data.records)) {
      newsArray = data.records;
    } else {
      // 단일 객체인 경우 배열로 변환
      newsArray = [data];
    }
    
    // 웹훅 응답 데이터를 컴포넌트에서 사용할 형태로 변환
    const transformedData = newsArray.map((item, index) => {
      // 필드명이 다를 수 있으므로 여러 가능성을 확인
      const title = item.title || item.Title || item.subject || '제목 없음';
      const content = item.content || item.Content || item.body || item.description || '내용 없음';
      const url = item.url || item.URL || item.link || item.source_url || '#';
      const summary = item.summary || item.Summary || item.description || item.preview || '요약 없음';
      const time = item.time || item.Time || item.date || item.timestamp || item.created_at || '시간 정보 없음';
      const category = item.category || item.Category || item.type || '기타';
      
      return {
        id: item.id || item.ID || `webhook-${index + 1}`,
        title: title,
        summary: summary,
        time: time,
        category: category,
        content: content,
        source: extractDomainFromUrl(url),
        url: url,
        image: getCategoryEmoji(category)
      };
    });

    return transformedData;
  } catch (error) {
    console.error('웹훅 API 호출 중 오류 발생:', error);
    throw error;
  }
};

// 홈페이지에서 사용할 기본 app 식별자
const LEGAL_POLICY_APP = 'homepage';

// 문서 유형 → legal_policies.name 값 매핑 (모든 프로젝트 공통)
export const LEGAL_DOC_NAMES = {
  policy: '개인정보처리방침',
  terms: '이용약관',
};

// Supabase public.legal_policies 테이블에서 app + name으로 정책 본문 조회
const fetchLegalPolicy = async (name, app = LEGAL_POLICY_APP) => {
  const { data, error } = await supabase
    .from('legal_policies')
    .select('content')
    .eq('app', app)
    .eq('name', name)
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data?.content || null;
};

// URL 세그먼트 ↔ doc_key 매핑.
//
// ★2026-09-07 사장님 지시★ — "/support/dallime/policy 현재 생성이 잘못 되고 있다.
// /support/dallime/privacy로 리디렉션해라."
//
// 종전에는 개인정보처리방침만 **예외적으로** `doc_key='privacy'` 를 URL `policy` 로
// 바꿔 내보냈다. 그 특례를 없앤다 — 이제 ★URL 세그먼트 = doc_key★ 로 전부 같다.
// 특례가 하나 있으면 링크 만드는 쪽·읽는 쪽·프리렌더 세 군데가 항상 같이 움직여야
// 하는데, 실제로 어긋나서 이 지시가 나왔다.
//
// ★`policy` 는 지우지 않고 **구주소 별칭으로 남긴다**★ — 앱 스토어 등재·외부 링크에
// 이미 나가 있을 수 있어서, 들어오면 계속 읽히고 정본 주소로 보내야 한다
// (SupportLegal.js 가 정본으로 replace 이동, 프리렌더가 정적 리디렉션 stub 생성).
const URL_TO_DOCKEY = { policy: 'privacy' };
/** 구주소 → 정본 URL 세그먼트. 새 별칭이 생기면 여기만 늘린다. */
export const LEGACY_URL_SEGMENTS = { policy: 'privacy' };
export const docKeyFromUrlSegment = (seg) => URL_TO_DOCKEY[seg] || seg;
/** doc_key 가 곧 URL 세그먼트다(특례 없음). */
export const urlSegmentFromDocKey = (key) => key;

// 고객센터 문서 노출 정렬 우선순위 (목록에 없는 doc_key는 뒤로 정렬)
const DOC_ORDER = [
  'terms',
  'privacy',
  'safety_checklist',
  'real_info_pledge',
  'report_system',
  'ip_location_notice',
];
const docRank = (key) => {
  const i = DOC_ORDER.indexOf(key);
  return i === -1 ? DOC_ORDER.length : i;
};

// 프로젝트(app)의 활성 법적 문서 1건을 doc_key로 조회 — SupportLegal 페이지용
//   반환: { name, content, doc_key, version } | null
export const fetchLegalDocByKey = async (app, docKey) => {
  if (!app || !docKey) return null;
  try {
    const { data, error } = await supabase
      .from('legal_policies')
      .select('name, content, doc_key, version')
      .eq('app', app)
      .eq('doc_key', docKey)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data || null;
  } catch (err) {
    console.error(`fetchLegalDocByKey(${app}, ${docKey}) error:`, err);
    return null;
  }
};

// 프로젝트(app)의 활성 법적 문서 목록 — 고객센터 "약관·정책" 섹션용
//   반환: [{ doc_key, name, version }] (DOC_ORDER 우선 정렬)
export const fetchLegalDocList = async (app) => {
  if (!app) return [];
  try {
    const { data, error } = await supabase
      .from('legal_policies')
      .select('doc_key, name, version')
      .eq('app', app)
      .eq('is_active', true);
    if (error) throw error;
    return (data || [])
      .slice()
      .sort((a, b) => docRank(a.doc_key) - docRank(b.doc_key));
  } catch (err) {
    console.error(`fetchLegalDocList(${app}) error:`, err);
    return [];
  }
};

// Privacy Policy 데이터 가져오기 (Supabase, app='homepage')
export const fetchPrivacyPolicy = async () => {
  try {
    return await fetchLegalPolicy(LEGAL_DOC_NAMES.policy);
  } catch (err) {
    console.error('fetchPrivacyPolicy error:', err);
    return null;
  }
};

// Terms 약관 데이터 가져오기 (Supabase, app='homepage')
export const fetchTerms = async () => {
  try {
    return await fetchLegalPolicy(LEGAL_DOC_NAMES.terms);
  } catch (err) {
    console.error('fetchTerms error:', err);
    return null;
  }
};

// n8n 웹훅에서 밈 케이스 타이틀 가져오기
export const fetchMemeCaseTitles = async () => {
  try {
    // 캐시 방지를 위해 URL에 쿼리 파라미터 추가
    const cacheBuster = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const webhookUrl = `https://bshong-n8n.ngrok.io/webhook/4c5bb8d1-d2a4-4fc3-9388-3de2e711e98c?_=${cacheBuster}`;
    
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-Requested-With': 'XMLHttpRequest'
      },
      cache: 'no-store', // fetch API의 캐시 방지 옵션
      body: JSON.stringify({
        timestamp: new Date().toISOString(),
        cacheBuster: cacheBuster
      })
    });

    if (!response.ok) {
      throw new Error(`n8n webhook error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // 디버깅: 전체 응답 로그 출력
    console.log('n8n 웹훅 응답 전체:', data);
    
    // n8n 웹훅 응답 구조에 따라 데이터 추출
    // 다양한 응답 형식 지원
    let fields = null;
    
    // 1. 직접 fields 객체가 있는 경우
    if (data.fields && typeof data.fields === 'object') {
      fields = data.fields;
    }
    // 2. records 배열이 있는 경우 (Airtable 형식)
    else if (data.records && Array.isArray(data.records) && data.records.length > 0) {
      const latestRecord = data.records[0];
      fields = latestRecord.fields || latestRecord;
    }
    // 3. data 배열이 있는 경우
    else if (data.data && Array.isArray(data.data) && data.data.length > 0) {
      const latestRecord = data.data[0];
      fields = latestRecord.fields || latestRecord;
    }
    // 4. 배열로 직접 반환된 경우
    else if (Array.isArray(data) && data.length > 0) {
      const latestRecord = data[0];
      fields = latestRecord.fields || latestRecord;
    }
    // 5. 단일 객체인 경우 (fields가 직접 포함)
    else if (typeof data === 'object' && data !== null) {
      // title1, title2, title3이 직접 있는지 확인
      if (data.title1 || data.title2 || data.title3) {
        fields = data;
      } else {
        // 다른 필드명으로 시도
        fields = data;
      }
    }
    
    if (!fields) {
      console.warn('n8n 웹훅에서 필드를 찾을 수 없습니다. 응답 구조:', JSON.stringify(data, null, 2));
      return null;
    }
    
    // title1, title2, title3 추출 (다양한 필드명 시도) - 타이틀과 필드명을 함께 저장
    const titleData = [
      { title: fields.title1 || fields.Title1 || fields['title1'], field: 'title1' },
      { title: fields.title2 || fields.Title2 || fields['title2'], field: 'title2' },
      { title: fields.title3 || fields.Title3 || fields['title3'], field: 'title3' }
    ].filter(item => item.title && String(item.title).trim() !== ''); // 빈 값 제거

    console.log('추출된 타이틀 데이터:', titleData);

    if (titleData.length < 3) {
      console.warn('title1, title2, title3 중 3개 모두의 값이 필요합니다. 추출된 타이틀:', titleData);
      return null;
    }

    // 3개 모두 반환 (랜덤 선택 제거)
    console.log('최종 선택된 타이틀:', titleData);
    return titleData;
  } catch (error) {
    console.error('n8n 웹훅 호출 중 오류 발생:', error);
    return null;
  }
};

// 에러 처리를 위한 기본 뉴스 데이터
export const getDefaultNewsData = () => {
  return {
    namely: [
      {
        id: 1,
        title: 'AI 기반 자동화 워크플로우 시장 급성장',
        summary: '글로벌 AI 자동화 시장이 2024년 30% 성장하며 새로운 비즈니스 모델을 제시하고 있습니다.',
        source: 'TechCrunch',
        time: '2시간 전',
        category: '기술',
        image: '🤖',
        content: '글로벌 AI 자동화 시장이 2024년 30% 성장하며 새로운 비즈니스 모델을 제시하고 있습니다. 이는 기업들의 디지털 전환 가속화와 AI 기술의 성숙도 향상이 주요 원인으로 분석됩니다.'
      },
      {
        id: 2,
        title: '스타트업 자동화 도구 도입률 3배 증가',
        summary: '중소기업들의 업무 자동화 도구 도입이 급증하며 생산성 향상 효과를 보이고 있습니다.',
        source: 'Forbes',
        time: '4시간 전',
        category: '비즈니스',
        image: '🚀',
        content: '중소기업들의 업무 자동화 도구 도입이 급증하며 생산성 향상 효과를 보이고 있습니다. 특히 인력 부족 문제를 해결하는 데 큰 도움이 되고 있다는 평가입니다.'
      },
      {
        id: 3,
        title: '정부 디지털 전환 정책 발표',
        summary: '정부가 공공기관 디지털 전환을 위한 AI 워크플로우 도입 계획을 발표했습니다.',
        source: '연합뉴스',
        time: '6시간 전',
        category: '정책',
        image: '🏛️',
        content: '정부가 공공기관 디지털 전환을 위한 AI 워크플로우 도입 계획을 발표했습니다. 이를 통해 공공 서비스의 효율성과 접근성이 크게 향상될 것으로 기대됩니다.'
      }
    ],
    secondary: [
      {
        id: 4,
        title: '회계 자동화로 업무 효율성 40% 향상',
        summary: 'AI 기반 회계 자동화 도구를 도입한 기업들이 평균 40%의 업무 효율성 향상을 경험했습니다.',
        source: 'Business Insider',
        time: '1일 전',
        category: '회계',
        image: '📊',
        content: 'AI 기반 회계 자동화 도구를 도입한 기업들이 평균 40%의 업무 효율성 향상을 경험했습니다. 특히 반복적인 업무의 자동화를 통해 인력의 고부가가치 업무에 집중할 수 있게 되었습니다.'
      },
      {
        id: 5,
        title: '데이터 분석 자동화 트렌드 분석',
        summary: '빅데이터 분석 자동화 도구의 시장 점유율이 지속적으로 증가하고 있습니다.',
        source: 'ZDNet',
        time: '1일 전',
        category: '분석',
        image: '📈',
        content: '빅데이터 분석 자동화 도구의 시장 점유율이 지속적으로 증가하고 있습니다. 기업들이 데이터 기반 의사결정의 중요성을 인식하면서 관련 도구의 도입이 가속화되고 있습니다.'
      },
      {
        id: 6,
        title: '마케팅 자동화 ROI 300% 달성',
        summary: '마케팅 자동화 도구를 활용한 기업들이 평균 300%의 ROI를 달성했다는 보고서가 발표되었습니다.',
        source: 'Marketing Land',
        time: '2일 전',
        category: '마케팅',
        image: '📢',
        content: '마케팅 자동화 도구를 활용한 기업들이 평균 300%의 ROI를 달성했다는 보고서가 발표되었습니다. 개인화된 마케팅과 고객 세분화를 통한 효과적인 캠페인 운영이 주요 성공 요인으로 분석됩니다.'
      }
    ],
    cta: [
      {
        id: 7,
        title: '지금 바로 AI 워크플로우 체험해보세요',
        summary: '무료 체험으로 비즈니스 자동화의 혁신을 경험하고, 30일 무료 체험 기회를 놓치지 마세요.',
        source: 'FLOWTO.AI',
        time: '지금',
        category: '체험',
        image: '🎯',
        ctaText: '무료 체험하기',
        content: '무료 체험으로 비즈니스 자동화의 혁신을 경험하고, 30일 무료 체험 기회를 놓치지 마세요. 전문가 상담과 함께 맞춤형 솔루션을 찾아보실 수 있습니다.'
      },
      {
        id: 8,
        title: '전문가 상담으로 맞춤형 솔루션 찾기',
        summary: '비즈니스 특성에 맞는 AI 워크플로우를 전문가와 상담하여 찾아보세요. 무료 상담 서비스를 제공합니다.',
        source: 'FLOWTO.AI',
        time: '지금',
        category: '상담',
        image: '💼',
        ctaText: '상담 신청하기',
        content: '비즈니스 특성에 맞는 AI 워크플로우를 전문가와 상담하여 찾아보세요. 무료 상담 서비스를 제공합니다. 경험 많은 컨설턴트가 귀하의 비즈니스에 최적화된 솔루션을 제안해드립니다.'
      },
      {
        id: 9,
        title: '성공 사례를 확인하고 시작하세요',
        summary: '다른 기업들의 성공 사례를 확인하고, 당신의 비즈니스도 자동화의 혜택을 누려보세요.',
        source: 'FLOWTO.AI',
        time: '지금',
        category: '성공사례',
        image: '🏆',
        ctaText: '성공사례 보기',
        content: '다른 기업들의 성공 사례를 확인하고, 당신의 비즈니스도 자동화의 혜택을 누려보세요. 다양한 업종과 규모의 기업들이 어떻게 자동화를 통해 성과를 달성했는지 확인해보실 수 있습니다.'
      }
    ]
  };
};
